import { useRef, useState } from "react";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { BusyIndicator } from "@/components/BusyIndicator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Notice } from "@/components/Notice";
import { useConfirm } from "@/components/ConfirmDialog";
import { useFileViewer } from "@/components/FileViewerDialog";
import {
  tickets as ticketsApi,
  type TicketAttachment,
  type TicketAttachmentSource,
} from "@/features/tickets/api/tickets.api";
import { ATTACHMENT_ACCEPT } from "@/features/conversations";
import {
  AttachmentDropzone,
  validateFiles,
} from "@/features/tickets/components/TicketAttachments";
import { AttachmentGrid, AttachmentTile } from "@/components/AttachmentTile";
import { isPreviewableFile } from "@/utils/files";

const SOURCE_LABELS: Record<TicketAttachmentSource, string> = {
  ticket: "Uploaded to the ticket",
  description: "In the description",
  comment: "From a comment",
  reply: "From a reply",
};

export function TicketAttachmentsCard({
  ticketId,
  attachments,
  totalCount,
  onChanged,
  onJumpToMessage,
}: {
  ticketId: string;
  attachments: TicketAttachment[];
  totalCount: number;
  onChanged: () => Promise<void> | void;
  onJumpToMessage?: (messageId: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const confirm = useConfirm();
  const viewFile = useFileViewer();

  const ownCount = attachments.filter((a) => a.source === "ticket").length;

  async function upload(files: File[]) {
    const { accepted, errors } = validateFiles(files, ownCount);
    setError(errors.length ? errors.join(" ") : null);
    if (!accepted.length) return;

    setBusy(true);
    try {
      for (const file of accepted) {
        await ticketsApi.uploadAttachment(ticketId, file);
      }
      await onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed.");
    } finally {
      setBusy(false);
    }
  }

  function view(a: TicketAttachment) {
    setError(null);
    viewFile({
      name: a.filename,
      load: () => ticketsApi.attachmentFile(a.id),
      errorMessage: "Could not open the attachment.",
    });
  }

  async function download(a: TicketAttachment) {
    setError(null);
    try {
      await ticketsApi.downloadAttachment(a.id, a.filename);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Download failed.");
    }
  }

  async function remove(a: TicketAttachment) {
    if (
      !(await confirm({
        description: `Remove ${a.filename}?`,
        confirmLabel: "Remove",
        destructive: true,
      }))
    )
      return;
    setBusy(true);
    setError(null);
    try {
      await ticketsApi.deleteAttachment(ticketId, a.id);
      await onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed.");
    } finally {
      setBusy(false);
    }
  }

  function noteFor(a: TicketAttachment) {
    const label = SOURCE_LABELS[a.source];
    const jumpable = a.sourceMessageId != null && onJumpToMessage != null;
    if (!jumpable) return label;
    return (
      <button
        type="button"
        onClick={() => onJumpToMessage?.(a.sourceMessageId as string)}
        className="text-brand-600 hover:underline"
      >
        {label} →
      </button>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 border-b py-3">
        <CardTitle className="text-base font-semibold text-slate-900">
          Attachments
          <span className="ml-2 text-sm font-normal text-slate-400">
            {totalCount} file{totalCount === 1 ? "" : "s"}
          </span>
        </CardTitle>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          {busy ? <Spinner /> : <Upload />}
          {busy ? "Uploading…" : "Upload file"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4 pt-6">
        {error && <Notice tone="error">{error}</Notice>}

        {attachments.length === 0 ? (
          <AttachmentDropzone onFiles={upload} disabled={busy} busy={busy} />
        ) : (
          <>
            {busy && (
              <div className="rounded-md border border-primary bg-primary/10 px-4 py-3">
                <BusyIndicator label="Uploading…" />
              </div>
            )}
            <AttachmentGrid>
              {attachments.map((a) => (
                <AttachmentTile
                  key={a.id}
                  filename={a.filename}
                  contentType={a.contentType}
                  sizeBytes={a.sizeBytes}
                  note={noteFor(a)}
                  disabled={busy}
                  onView={isPreviewableFile(a.filename, a.contentType) ? () => view(a) : undefined}
                  onDownload={() => download(a)}
                  onRemove={a.canDelete ? () => remove(a) : undefined}
                />
              ))}
            </AttachmentGrid>
          </>
        )}

        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ATTACHMENT_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            e.target.value = "";
            if (files.length) upload(files);
          }}
        />
      </CardContent>
    </Card>
  );
}
